import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/shared/components/Input";
import { Button } from "@/shared/components/Button";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";

// Limite espelhando a coluna do banco (migration 0016): ministerios.nome varchar(100).
const LIMITE_NOME = 100;

const schema = z.object({
  nome: z
    .string()
    .min(3, "Nome muito curto.")
    .max(LIMITE_NOME, `O nome pode ter no máximo ${LIMITE_NOME} caracteres.`),
  descricao: z.string(),
});

type Valores = z.infer<typeof schema>;

interface FormularioMinisterioProps {
  /** Presente = edição; ausente = criação. */
  valoresIniciais?: { nome: string; descricao: string | null };
  carregando: boolean;
  erro?: string | null;
  onSubmit: (dados: { nome: string; descricao: string | null }) => void;
  /** Quando informado, mostra o botão "Fechar" (fechamento explícito, sem depender de clicar fora). */
  onFechar?: () => void;
}

/** Formulário de criar/editar ministério (Núcleo — 03-Núcleo §5). */
export function FormularioMinisterio({
  valoresIniciais,
  carregando,
  erro,
  onSubmit,
  onFechar,
}: FormularioMinisterioProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Valores>({
    resolver: zodResolver(schema),
    defaultValues: { nome: "", descricao: "" },
  });

  useEffect(() => {
    if (valoresIniciais) {
      reset({ nome: valoresIniciais.nome, descricao: valoresIniciais.descricao ?? "" });
    }
  }, [valoresIniciais, reset]);

  const nomeAtual = watch("nome") ?? "";

  function submit(dados: Valores) {
    onSubmit({ nome: dados.nome.trim(), descricao: dados.descricao.trim() || null });
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1">
        <Input
          label="Nome do ministério"
          placeholder="Ex.: Louvor, Infantil, Biblioteca"
          maxLength={LIMITE_NOME}
          erro={errors.nome?.message}
          {...register("nome")}
        />
        <p
          className={`text-right text-xs ${
            nomeAtual.length >= LIMITE_NOME ? "font-medium text-warning" : "text-muted"
          }`}
        >
          {nomeAtual.length}/{LIMITE_NOME}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="descricao-ministerio" className="text-sm font-medium text-text">
          Descrição (opcional)
        </label>
        <textarea
          id="descricao-ministerio"
          rows={3}
          placeholder="O que este ministério faz na igreja."
          className="rounded-md border border-transparent bg-surface-2 px-4 py-2.5 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
          {...register("descricao")}
        />
      </div>

      {!valoresIniciais && (
        <p className="rounded-md bg-primary/10 px-3 py-2 text-xs leading-relaxed text-primary">
          A função <span className="font-semibold">"Líder"</span> é criada automaticamente — depois
          é só nomear quem lidera.
        </p>
      )}

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <div className="mt-2 flex flex-col gap-2">
        <Button type="submit" carregando={carregando} className="w-full">
          {valoresIniciais ? "Salvar alterações" : "Criar ministério"}
        </Button>
        <BotaoVoltar onClick={onFechar} disabled={carregando} />
      </div>
    </form>
  );
}
